import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Avatar,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  useTheme,
  Stack,
  useMediaQuery,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Grid,
} from "@mui/material";
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  AdminPanelSettings as AdminIcon,
  SupervisedUserCircle as SuperAdminIcon,
  Group as TeamIcon,
  CheckCircle,
  Pending,
  Cancel,
  Email,
  Phone,
  Badge,
  Security,
  Edit,
} from "@mui/icons-material";
import { url } from "../constants/url";
import { useThemeToggle } from "../context/ThemeContext";

const Profile = ({ userData }) => {
  const theme = useTheme();
  const { mode } = useThemeToggle();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const userRole = userData?.role || "";

  const [companyData, setCompanyData] = useState([]);
  const [openEdit, setOpenEdit] = useState(false);
  const [teamMembersOpen, setTeamMembersOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: userData?.name || "",
    email: userData?.email || "",
    phone: userData?.phone || "",
    role: userData?.role || "",
  });

  // Theme colors from context
  const primaryColor = theme.palette.primary.main;

  const fetchCompanyData = async () => {
    try {
      let endpoint;

      if (userData.role === "admin") {
        endpoint = `${url}/api/company/fetch-companies?userId=${userData._id}`;
      } else {
        endpoint = `${url}/api/company/fetch-company-assigned-to?userId=${userData._id}`;
      }

      console.log("🔄 Fetching from:", endpoint);
      const response = await fetch(endpoint);
      const data = await response.json();
      console.log("📦 API Response:", data);
      setCompanyData(data?.data || []);
    } catch (error) {
      console.error("❌ Error fetching company data:", error);
    }
  };

  useEffect(() => {
    if (userData?._id) {
      fetchCompanyData();
    }
  }, [userData]);

  const handleEditOpen = () => {
    setFormData({
      name: userData?.name || "",
      email: userData?.email || "",
      phone: userData?.phone || "",
      role: userData?.role || "",
    });
    setOpenEdit(true);
  };

  const handleEditClose = () => {
    setOpenEdit(false);
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleUpdate = async () => {
    try {
      const response = await fetch(
        `${url}/api/users/update-user/${userData._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      const updatedUser = await response.json();

      if (response.ok) {
        alert("Profile updated successfully!");
        setOpenEdit(false);
      } else {
        alert(updatedUser.message || "Failed to update profile");
      }
    } catch (error) {
      console.error("❌ Error updating profile:", error);
      alert("Error updating profile");
    }
  };

  const handleViewTeamMembers = () => {
    setTeamMembersOpen(true);
  };

  const handleTeamMembersClose = () => {
    setTeamMembersOpen(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle sx={{ color: "success.main", fontSize: 16 }} />;
      case "pending":
        return <Pending sx={{ color: "warning.main", fontSize: 16 }} />;
      case "rejected":
        return <Cancel sx={{ color: "error.main", fontSize: 16 }} />;
      default:
        return <Pending sx={{ color: "text.secondary", fontSize: 16 }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "success";
      case "pending":
        return "warning";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "admin":
        return "primary";
      case "superadmin":
        return "primary";
      case "teammember":
        return "primary";
      default:
        return "default";
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "admin":
        return <AdminIcon sx={{ fontSize: 16 }} />;
      case "superadmin":
        return <SuperAdminIcon sx={{ fontSize: 16 }} />;
      default:
        return <PersonIcon sx={{ fontSize: 16 }} />;
    }
  };

  // Info Item Component for consistent styling
  const InfoItem = ({ icon, label, value, subValue }) => (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
      <Box sx={{ color: "text.secondary", mt: 0.2 }}>{icon}</Box>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
        <Typography
          variant="body1"
          fontWeight={500}
          sx={{ mb: subValue ? 0.5 : 0 }}
        >
          {value || "Not provided"}
        </Typography>
        {subValue && (
          <Typography variant="body2" color="text.secondary">
            {subValue}
          </Typography>
        )}
      </Box>
    </Stack>
  );

  // Section Header Component
  const SectionHeader = ({ icon, title, action }) => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ mb: 3 }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        {icon}
        <Typography variant="h6" fontWeight={600} color="primary.main">
          {title}
        </Typography>
      </Stack>
      {action}
    </Stack>
  );

  // Get the first company (assuming user is associated with one company)
  const currentCompany = companyData.length > 0 ? companyData[0] : null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        py: { xs: 2, sm: 3, md: 4 },
        px: { xs: 1, sm: 2 },
        mt: -5,
        ml: -5,
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
        {/* Main Profile Card */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: "background.paper",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            {/* Personal Information Section */}
            <SectionHeader
              icon={<PersonIcon sx={{ color: "primary.main", fontSize: 28 }} />}
              title="Personal Information"
              action={
                <Button
                  variant="outlined"
                  onClick={handleEditOpen}
                  startIcon={<Edit />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                    borderColor: "primary.main",
                    color: "primary.main",
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor:
                        mode === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(5,7,18,0.04)",
                    },
                  }}
                >
                  Edit Profile
                </Button>
              }
            />

            <Grid container spacing={4}>
              {/* Avatar and Basic Info */}
              <Grid item xs={12} md={4}>
                <Stack
                  alignItems="center"
                  spacing={2}
                  sx={{ textAlign: "center" }}
                >
                  <Avatar
                    sx={{
                      width: { xs: 80, sm: 100, md: 120 },
                      height: { xs: 80, sm: 100, md: 120 },
                      bgcolor: "primary.main",
                      color: mode === "light" ? "#fff" : "#0f172a",
                    }}
                  >
                    <PersonIcon sx={{ fontSize: { xs: 40, sm: 50, md: 60 } }} />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      gutterBottom
                      color="text.primary"
                    >
                      {userData?.name || "No Name"}
                    </Typography>
                    <Chip
                      label={userData?.role || "User"}
                      sx={{
                        fontWeight: 600,
                        bgcolor: "primary.main",
                        color: mode === "light" ? "#fff" : "#0f172a",
                      }}
                      size={isMobile ? "small" : "medium"}
                    />
                  </Box>
                </Stack>
              </Grid>

              {/* Contact Information */}
              <Grid item xs={12} md={8}>
                <Stack spacing={2}>
                  <InfoItem
                    icon={<Email sx={{ fontSize: 20 }} />}
                    label="Email Address"
                    value={userData?.email}
                  />
                  <InfoItem
                    icon={<Phone sx={{ fontSize: 20 }} />}
                    label="Phone Number"
                    value={userData?.phone}
                  />
                  <InfoItem
                    icon={<Badge sx={{ fontSize: 20 }} />}
                    label="User Role"
                    value={userData?.role}
                  />
                </Stack>
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* Company Information Section */}
            {currentCompany && (
              <>
                {/* Header */}
                <SectionHeader
                  icon={
                    <BusinessIcon
                      sx={{ color: "primary.main", fontSize: 28 }}
                    />
                  }
                  title="Company Information"
                />

                {/* Company Information */}
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      <InfoItem
                        icon={<BusinessIcon sx={{ fontSize: 20 }} />}
                        label="Company Name"
                        value={currentCompany.name}
                      />
                      <InfoItem
                        icon={<Badge sx={{ fontSize: 20 }} />}
                        label="Company Status"
                        value={
                          <Chip
                            label={currentCompany.status}
                            color={getStatusColor(currentCompany.status)}
                            icon={getStatusIcon(currentCompany.status)}
                            size="small"
                            sx={{
                              textTransform: "capitalize",
                              fontWeight: 600,
                            }}
                          />
                        }
                      />
                      <InfoItem
                        icon={<TeamIcon sx={{ fontSize: 20 }} />}
                        label="Total Team Members"
                        value={currentCompany.teamMembers?.length || 0}
                      />
                    </Stack>
                  </Grid>
                </Grid>

                {/* Admin Information (Placed BELOW Company Info) */}
                {userRole !== "admin" && (
                  <>
                    {/* Divider BELOW company info */}
                    <Divider sx={{ my: 4 }} />
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{ mb: 2, color: "primary.main" }}
                    >
                      Company Admin
                    </Typography>

                    {currentCompany.createdBy ? (
                      <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                          >
                            <Avatar
                              sx={{
                                width: 50,
                                height: 50,
                                bgcolor: "primary.main",
                              }}
                            >
                              <PersonIcon />
                            </Avatar>
                            <Box>
                              <Typography
                                variant="body1"
                                fontWeight={600}
                                color="text.primary"
                              >
                                {currentCompany.createdBy.name}
                              </Typography>
                              <Chip
                                icon={getRoleIcon(
                                  currentCompany.createdBy.role
                                )}
                                label={currentCompany.createdBy.role}
                                color={getRoleColor(
                                  currentCompany.createdBy.role
                                )}
                                size="small"
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          </Stack>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <InfoItem
                            icon={<Email sx={{ fontSize: 18 }} />}
                            label="Admin Email"
                            value={currentCompany.createdBy.email}
                          />
                        </Grid>
                      </Grid>
                    ) : (
                      <Typography color="text.secondary" fontStyle="italic">
                        No admin information available
                      </Typography>
                    )}
                  </>
                )}

                {/* Divider BELOW admin info */}
                <Divider sx={{ my: 4 }} />

                {/* Approved By Section */}
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ mb: 2, color: "primary.main" }}
                  >
                    Approved By
                  </Typography>

                  {currentCompany.approvedBy ? (
                    <Grid container spacing={4}>
                      <Grid item xs={12} md={6}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar
                            sx={{
                              width: 50,
                              height: 50,
                              bgcolor: "primary.main",
                            }}
                          >
                            <Security />
                          </Avatar>
                          <Box>
                            <Typography
                              variant="body1"
                              fontWeight={600}
                              color="text.primary"
                            >
                              {currentCompany.approvedBy.name}
                            </Typography>
                            <Chip
                              icon={getRoleIcon(currentCompany.approvedBy.role)}
                              label={currentCompany.approvedBy.role}
                              color={getRoleColor(
                                currentCompany.approvedBy.role
                              )}
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        </Stack>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <InfoItem
                          icon={<Email sx={{ fontSize: 18 }} />}
                          label="Super Admin Email"
                          value={currentCompany.approvedBy.email}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography color="text.secondary" fontStyle="italic">
                      Not approved yet
                    </Typography>
                  )}
                </Box>

                {/* Divider BELOW approved info */}
                <Divider sx={{ my: 4 }} />

                {/* Team Members Section */}
                <Box>
                  <SectionHeader
                    icon={
                      <TeamIcon sx={{ color: "primary.main", fontSize: 28 }} />
                    }
                    title="Team Members"
                    action={
                      (userData?.role === "admin" ||
                        userData?.role === "superadmin") && (
                        <Button
                          variant="contained"
                          onClick={handleViewTeamMembers}
                          startIcon={<TeamIcon />}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            bgcolor: "primary.main",
                            "&:hover": {
                              bgcolor: mode === "dark" ? "#1e293b" : "#0a0e2c",
                            },
                          }}
                        >
                          View All Members
                        </Button>
                      )
                    }
                  />

                  {!(
                    userData?.role === "admin" ||
                    userData?.role === "superadmin"
                  ) && (
                    <Box sx={{ textAlign: "center", py: 2 }}>
                      <Typography variant="body1" color="text.secondary">
                        Total{" "}
                        <strong>
                          {currentCompany.teamMembers?.length || 0}
                        </strong>{" "}
                        team members in your company
                      </Typography>
                    </Box>
                  )}
                </Box>
              </>
            )}

            {/* No Company Message */}
            {/* {!currentCompany && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <BusinessIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Company Associated
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {userData?.role === "admin" 
                    ? "You haven't created any companies yet." 
                    : "You are not associated with any company yet."}
                </Typography>
              </Box>
            )} */}
          </CardContent>
        </Card>

        {/* Team Members Modal */}
        <Dialog
          open={teamMembersOpen}
          onClose={handleTeamMembersClose}
          fullWidth
          maxWidth="md"
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              bgcolor: "background.paper",
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 700, bgcolor: "background.paper" }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TeamIcon sx={{ color: "primary.main" }} />
              <Typography variant="h6" color="text.primary">
                Team Members - {currentCompany?.name}
              </Typography>
              <Chip
                label={currentCompany?.teamMembers?.length || 0}
                color="primary"
                size="small"
              />
            </Stack>
          </DialogTitle>
          <Divider />

          <DialogContent sx={{ mt: 2, bgcolor: "background.paper" }}>
            {currentCompany?.teamMembers &&
            currentCompany.teamMembers.length > 0 ? (
              <Card
                variant="outlined"
                sx={{ borderRadius: 2, bgcolor: "background.paper" }}
              >
                <List sx={{ p: 0 }}>
                  {currentCompany.teamMembers.map((member, index) => (
                    <React.Fragment key={member.userId?._id || index}>
                      <ListItem sx={{ py: 2, px: 2.5 }}>
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: "primary.main",
                              width: 44,
                              height: 44,
                            }}
                          >
                            <PersonIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1}
                              alignItems={{ xs: "flex-start", sm: "center" }}
                            >
                              <Typography
                                variant="body1"
                                fontWeight={600}
                                color="text.primary"
                              >
                                {member.userId?.name || "Unknown User"}
                              </Typography>
                              <Chip
                                icon={getRoleIcon(member.userId?.role)}
                                label={member.userId?.role || "teammember"}
                                color={getRoleColor(member.userId?.role)}
                                size="small"
                                variant="outlined"
                              />
                            </Stack>
                          }
                          secondary={
                            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                <Email sx={{ fontSize: 14, mr: 0.5 }} />
                                {member.userId?.email || "No email"}
                              </Typography>
                              {member.userId?.phone && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  <Phone sx={{ fontSize: 14, mr: 0.5 }} />
                                  {member.userId.phone}
                                </Typography>
                              )}
                            </Stack>
                          }
                        />
                      </ListItem>
                      {index < currentCompany.teamMembers.length - 1 && (
                        <Divider />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              </Card>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <TeamIcon
                  sx={{
                    fontSize: 64,
                    color: "text.secondary",
                    mb: 2,
                    opacity: 0.5,
                  }}
                />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Team Members
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  There are no team members in this company yet.
                </Typography>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3, bgcolor: "background.paper" }}>
            <Button
              onClick={handleTeamMembersClose}
              variant="outlined"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                borderColor: "primary.main",
                color: "primary.main",
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Profile Dialog */}
        <Dialog
          open={openEdit}
          onClose={handleEditClose}
          fullWidth
          maxWidth="sm"
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              bgcolor: "background.paper",
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 700, color: "text.primary" }}>
            Edit Profile
          </DialogTitle>
          <Divider />

          <DialogContent sx={{ mt: 2 }}>
            <Stack spacing={2}>
              <TextField
                label="Name"
                name="name"
                fullWidth
                value={formData.name}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
              />
              <TextField
                label="Email"
                name="email"
                fullWidth
                disabled
                value={formData.email}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
              />
              <TextField
                label="Phone"
                name="phone"
                fullWidth
                value={formData.phone}
                onChange={handleChange}
                size={isMobile ? "small" : "medium"}
              />
              <TextField
                label="Role"
                name="role"
                fullWidth
                disabled
                value={formData.role}
                size={isMobile ? "small" : "medium"}
              />
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 2.5 }}>
            <Button
              onClick={handleEditClose}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                color: "text.secondary",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{
                bgcolor: "primary.main",
                textTransform: "none",
                px: 3,
                py: 1,
                borderRadius: 2,
                fontWeight: 600,
                "&:hover": {
                  bgcolor: mode === "dark" ? "#1e293b" : "#0a0e2c",
                },
              }}
              onClick={handleUpdate}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Profile;
